import { Chip as MaterialPill, ChipProps as MaterialPillProps } from '@mui/material';
import React, { FocusEventHandler, MouseEventHandler, useMemo } from 'react';

declare module '@mui/material/Chip' {
  interface ChipPropsSizeOverrides {
    small: true;
    medium: true;
    responsive: true;
  }
  interface ChipPropsVariantOverrides {
    outlined: true;
    filled: true;
  }

  interface ChipPropsColorOverrides {
    primary: false;
    secondary: false;

    red: true;
    grey: true;
    green: true;
  }
}

export interface PillProps extends MaterialPillProps {
  size?: 'medium' | 'small' | 'responsive';

  /**
   * If `true`, the component will take up the full width of its container.
   */
  fullWidth?: boolean;

  /** Component to render */
  component?: 'button' | 'div' | 'a';

  /** @see {HTMLAnchorElement} */
  href?: HTMLAnchorElement['href'];

  /** Mouse Event: onDelete */
  onDelete?: MouseEventHandler<HTMLButtonElement | HTMLDivElement | HTMLAnchorElement>;
  /** Mouse Event: onClick */
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLDivElement | HTMLAnchorElement>;
  /** Focus Event: onBlur */
  onBlur?: FocusEventHandler<HTMLButtonElement | HTMLDivElement | HTMLAnchorElement>;
  /** Focus Event: onFocus */
  onFocus?: FocusEventHandler<HTMLButtonElement | HTMLDivElement | HTMLAnchorElement>;
}

/**
 * The Pill
 *
 * @version [AIV]{version}[/AIV]
 * @author [SFP](https://gitlab.sfp.berlin/products/sparkasse.de/portals)
 * @see [https://styleguide.sparkasse.de/document/69#/ui-komponenten/pill]
 */
export const Pill = React.forwardRef<HTMLDivElement, PillProps>(
  (
    {
      variant = 'outlined',
      size = 'medium',
      clickable,
      onClick,
      label = '',
      onDelete,
      deleteIcon,
      fullWidth,
      ...props
    },
    ref
  ) => {
    const { component, href } = props;

    const _onDelete = useMemo(() => {
      if (!deleteIcon) {
        return undefined;
      }

      if (onDelete) {
        return onDelete;
      }

      if (onClick) {
        return onClick;
      }

      // set empty function because delete icon will not be shown if onDelete is undefined
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {};
    }, [deleteIcon, onDelete, onClick]);

    return (
      <MaterialPill
        variant={variant}
        size={size}
        clickable={clickable === true || !!onClick || (component === 'a' && !!href)}
        onClick={clickable !== false ? onClick : null}
        label={label}
        deleteIcon={deleteIcon}
        onDelete={_onDelete}
        {...props}
        sx={{
          ...(fullWidth ? { width: '100%' } : {}),
          ...(label
            ? {}
            : {
                '& .MuiChip-label': {
                  paddingLeft: '5px',
                  paddingRight: '5px',
                },
              }),
          ...props.sx,
        }}
        ref={ref}
      />
    );
  }
);

Pill.displayName = 'Pill';
